import timezoneService from '../services/timezoneService';
import { differenceInMinutes } from 'date-fns';
import { format } from 'date-fns-tz';

/**
 * Calculate duration for screenshots using smart gap detection
 * Follows timezone & sorting rules: uses TimezoneService for all time operations
 */

interface Screenshot {
  id: string;
  timestamp: Date | string;
  duration?: number;
  [key: string]: any;
}

interface ScreenshotWithCalculatedDuration extends Screenshot {
  calculatedDuration: number;
}

/**
 * Calculate duration between two screenshots in minutes
 * Uses TimezoneService for proper timezone handling
 */
const getMinutesBetween = (screenshot1: Screenshot, screenshot2: Screenshot): number => {
  const time1 = typeof screenshot1.timestamp === 'string' 
    ? timezoneService.convertUTCToUserTime(screenshot1.timestamp)
    : screenshot1.timestamp;
  
  const time2 = typeof screenshot2.timestamp === 'string'
    ? timezoneService.convertUTCToUserTime(screenshot2.timestamp)
    : screenshot2.timestamp;
  
  return Math.abs(differenceInMinutes(time2, time1));
};

/**
 * Calculate durations for an array of screenshots using smart gap detection
 * 
 * Algorithm:
 * - For each screenshot, calculate time gap to next screenshot
 * - If gap <= maxGap (interval * 2.5): use actual gap
 * - If gap > maxGap: user paused/stopped, use interval only
 * - Last screenshot: always use interval
 * 
 * @param screenshots - Array of screenshots sorted by timestamp
 * @param interval - User's screenshot interval in minutes (1, 5, 10, or 30)
 * @returns Array of screenshots with calculatedDuration field
 */
export const calculateDurationsWithGapDetection = (
  screenshots: Screenshot[],
  interval: number
): ScreenshotWithCalculatedDuration[] => {
  if (screenshots.length === 0) {
    return [];
  }

  // Max gap = interval * 2.5 to handle small timing variations
  // 2.5x multiplier chosen because:
  // - Allows for 50% timing variation (e.g., 5min interval can have 7.5min gaps)
  // - Handles delays in screenshot capture (network issues, system load)
  // - Balances between accuracy and tolerance
  const maxGap = interval * 2.5;

  console.log(`📊 Duration Calculator: Processing ${screenshots.length} screenshots with interval ${interval}min (maxGap: ${maxGap}min)`);

  return screenshots.map((screenshot, index) => {
    // Last screenshot always gets interval duration
    if (index === screenshots.length - 1) {
      return {
        ...screenshot,
        calculatedDuration: interval
      };
    }

    // Calculate gap to next screenshot
    const nextScreenshot = screenshots[index + 1];
    const gap = getMinutesBetween(screenshot, nextScreenshot);

    let duration: number;
    if (gap <= maxGap) {
      // Normal gap - use actual time
      duration = gap;
    } else {
      // Large gap - user paused/stopped - use interval only
      duration = interval;
    }

    return {
      ...screenshot,
      calculatedDuration: duration
    };
  });
};

/**
 * Calculate total time from screenshots with gap detection
 * 
 * @param screenshots - Array of screenshots
 * @param interval - User's screenshot interval in minutes
 * @returns Total time in minutes
 */
export const calculateTotalTime = (
  screenshots: Screenshot[],
  interval: number
): number => {
  const screenshotsWithDuration = calculateDurationsWithGapDetection(screenshots, interval);
  const totalMinutes = screenshotsWithDuration.reduce(
    (sum, screenshot) => sum + screenshot.calculatedDuration,
    0
  );

  console.log(`⏱️ Total time calculated: ${totalMinutes} minutes (${Math.round(totalMinutes / 60 * 100) / 100} hours)`);

  return totalMinutes;
};

/**
 * Format duration in human-readable format
 * 
 * @param minutes - Duration in minutes
 * @returns Formatted string like "2h 30m" or "45m"
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

/**
 * Group screenshots by date (in user's timezone) and calculate daily totals
 * 
 * @param screenshots - Array of screenshots
 * @param interval - User's screenshot interval in minutes
 * @returns Map of date -> { screenshots, totalDuration }
 */
export const groupByDateWithDurations = (
  screenshots: Screenshot[],
  interval: number
): Map<string, { screenshots: ScreenshotWithCalculatedDuration[], totalDuration: number }> => {
  // First calculate durations for all screenshots
  const screenshotsWithDuration = calculateDurationsWithGapDetection(screenshots, interval);
  
  // Then group by date in user's timezone
  const groups = new Map<string, { screenshots: ScreenshotWithCalculatedDuration[], totalDuration: number }>();
  
  screenshotsWithDuration.forEach(screenshot => {
    // Convert to user's timezone and get date key
    const zonedDate = timezoneService.convertUTCToUserTime(
      typeof screenshot.timestamp === 'string' ? screenshot.timestamp : screenshot.timestamp.toISOString()
    );
    
    const dateKey = format(zonedDate, 'yyyy-MM-dd');
    
    if (!groups.has(dateKey)) {
      groups.set(dateKey, { screenshots: [], totalDuration: 0 });
    }
    
    const group = groups.get(dateKey)!;
    group.screenshots.push(screenshot);
    group.totalDuration += screenshot.calculatedDuration;
  });
  
  console.log(`📅 Grouped into ${groups.size} days`);
  
  return groups;
};
